/**
 * `useBulkApprove` mutation hook.
 *
 * Wraps the raw `bulkApprove` service fn. The bulk-approve endpoint
 * returns per-asset results (`{ results: [{ assetId, success, reason? }] }`), not
 * full approval chain DTOs, so we can't seed the read cache — instead we invalidate the
 * whole asset-approval-chain namespace so any open asset detail/timeline refetches,
 * plus the folder files query (per-card `workflowProgress` shifts) and the folder
 * list/detail aggregates (`invalidateFolderAggregates`).
 *
 * The `results` array is returned to the caller so the toolbar can report a
 * per-asset success/failure summary. Caller owns error UX (the raw fn suppresses
 * the global error toast).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ASSET_APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/asset-approval-chain';
import { PROJECT_FOLDER_QUERY_KEYS } from '@/entities/project-folder';
import { track } from '@/shared/analytics';

import { bulkApprove } from './bulk-approval.mutations.api';
import { invalidateFolderAggregates } from './invalidate-folder-aggregates';

export { bulkApprove };

interface UseBulkApproveOptions {
  projectId: string;
  /** Folder currently in view — used to refresh its files list after approving. */
  folderId?: string;
}

export function useBulkApprove(options: UseBulkApproveOptions) {
  const queryClient = useQueryClient();
  const { projectId, folderId } = options;

  return useMutation({
    mutationFn: (assetIds: string[]) => bulkApprove(projectId, assetIds),
    onSuccess: (result) => {
      // Broad prefix invalidation — refresh any open asset-approval-chain query.
      void queryClient.invalidateQueries({
        queryKey: ASSET_APPROVAL_CHAIN_QUERY_KEYS.namespace(projectId),
      });
      if (folderId) {
        void queryClient.invalidateQueries({
          queryKey: PROJECT_FOLDER_QUERY_KEYS.files(projectId, folderId),
        });
      }
      invalidateFolderAggregates(queryClient, projectId, folderId);

      const approvedCount = result.results.filter((r) => r.success).length;
      track('assets_bulk_approved', {
        project_id: projectId,
        approved_count: approvedCount,
        skipped_count: result.results.length - approvedCount,
        total_count: result.results.length,
      });
    },
  });
}
